import { useEffect, useState, type FormEvent } from 'react'
import { Loader2, MapPin, Shuffle } from 'lucide-react'
import { Field, Modal } from '@/shared/ui/Modal'
import { Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import { UnitMap } from '@/shared/map/UnitMap'
import { outsideKenya } from '@/shared/map/geo'
import { changeUnitStatus, searchUnits, updateUnit } from './api'
import {
  UNIT_TYPE_LABEL, geometryKindOf,
  type UnitDetailResponse, type UnitResponse,
} from '@/shared/api/types'

/**
 * Edit the mutable fields. Type, code and parent are all immutable server-side.
 *
 * Two rules the backend enforces and this form has to respect, or every save is
 * a 400:
 *
 * - **`registeredVoters` is station-level.** `UnitService.update` rejects it on
 *   anything above a station — parents are rolled up from the stations beneath
 *   them. So the field is only rendered, and only sent, for a station.
 * - **A boundary owns its centroid.** `setBoundary` derives the pin and the area
 *   from the polygon. Typing coordinates on a unit that has a boundary
 *   desynchronises the two; backend prompt 9 turns that into a 400, and until it
 *   lands the form says so rather than letting it happen quietly.
 */
export function EditUnitDialog({
  detail, onClose, onSaved,
}: {
  detail: UnitDetailResponse | null
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState('')
  const [region, setRegion] = useState('')
  const [lat, setLat] = useState<number | ''>('')
  const [lon, setLon] = useState<number | ''>('')
  const [registered, setRegistered] = useState<number | ''>('')
  const [population, setPopulation] = useState<number | ''>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const u = detail?.unit
    if (!u) return
    setName(u.name)
    setRegion(u.region ?? '')
    setLat(u.latitude ?? '')
    setLon(u.longitude ?? '')
    setRegistered(u.registeredVoters ?? '')
    setPopulation(u.population ?? '')
    setError(null)
  }, [detail])

  const u = detail?.unit
  const isStation = u?.unitType === 'POLLING_STATION'
  const kind = geometryKindOf(u?.unitType)
  /**
   * `hasBoundary` arrives with backend prompt 9. Until then `areaSqKm` is an exact
   * proxy: it is written by `setBoundary` and by nothing else, so a non-null area
   * means a polygon is stored.
   */
  const hasBoundary = u?.hasBoundary ?? (u?.areaSqKm != null)
  const movedPin = u != null
    && ((lat === '' ? null : lat) !== (u.latitude ?? null)
      || (lon === '' ? null : lon) !== (u.longitude ?? null))

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!u) return
    setBusy(true); setError(null)
    try {
      // `version` becomes the If-Match header — a stale one is a 409, which is
      // the point: two people editing the same ward should not silently race.
      await updateUnit(u.id, u.version, {
        name: name.trim(),
        region: region.trim() || null,
        latitude: lat === '' ? null : lat,
        longitude: lon === '' ? null : lon,
        registeredVoters: isStation && registered !== '' ? registered : null,
        population: population === '' ? null : population,
      })
      onSaved()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const warn = outsideKenya(lat, lon)

  return (
    <Modal
      open={Boolean(detail)}
      onClose={onClose}
      width="lg"
      title={`Edit ${u?.name ?? 'unit'}`}
      description={u ? `${UNIT_TYPE_LABEL[u.unitType] ?? u.unitType} · ${u.code}` : undefined}
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="submit" form="edit-unit-form" className="erp-btn erp-btn-primary"
                  disabled={busy || !name.trim()}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save changes
          </button>
        </>
      }
    >
      <form id="edit-unit-form" onSubmit={submit} className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}

        <Field label="Type and code" hint="Immutable — shown for reference.">
          <input
            value={u ? `${UNIT_TYPE_LABEL[u.unitType] ?? u.unitType} · ${u.code}` : ''}
            disabled
            className="erp-input w-full opacity-60"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" required>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   className="erp-input w-full" maxLength={160} required />
          </Field>
          <Field label="Region">
            <input value={region} onChange={(e) => setRegion(e.target.value)}
                   className="erp-input w-full" maxLength={48} />
          </Field>
          {isStation && (
            <Field label="Registered voters">
              <input type="number" min={0} value={registered}
                     onChange={(e) => setRegistered(e.target.value === '' ? '' : Number(e.target.value))}
                     className="erp-input w-full" />
            </Field>
          )}
          <Field label="Population">
            <input type="number" min={0} value={population}
                   onChange={(e) => setPopulation(e.target.value === '' ? '' : Number(e.target.value))}
                   className="erp-input w-full" />
          </Field>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,16rem)] items-start">
          <UnitMap
            mode={kind === 'POINT' ? 'point' : 'boundary'}
            lat={lat === '' ? null : lat}
            lon={lon === '' ? null : lon}
            editable={kind === 'POINT'}
            onPick={(la, lo) => { setLat(la); setLon(lo) }}
            label={u?.name ?? 'unit'}
            className="h-48"
          />
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-1">
            <Field label="Latitude">
              <input type="number" step="0.000001" value={lat}
                     onChange={(e) => setLat(e.target.value === '' ? '' : Number(e.target.value))}
                     className="erp-input w-full font-mono" />
            </Field>
            <Field label="Longitude">
              <input type="number" step="0.000001" value={lon}
                     onChange={(e) => setLon(e.target.value === '' ? '' : Number(e.target.value))}
                     className="erp-input w-full font-mono" />
            </Field>
          </div>
        </div>

        {warn && <Note tone="bad">Those coordinates are outside Kenya — check the order.</Note>}

        {hasBoundary && movedPin && (
          <Note tone="bad">
            This unit has a boundary, and its pin is derived from that polygon. Moving
            the pin by hand leaves the two describing different places, and the stored
            area keeps describing the old shape. Replace the boundary instead — or
            remove it first, if the polygon is what is wrong.
          </Note>
        )}

        {!isStation && (
          <p className="text-erp-xs text-muted-foreground">
            Station count and rolled-up voter totals are maintained by the server and
            reconciled nightly; they are not editable here.
          </p>
        )}
      </form>
    </Modal>
  )
}

/**
 * Merge or dissolve. There is no delete — historical voters and tallies still
 * reference a unit, so it changes status and keeps its id forever.
 */
export function ChangeStatusDialog({
  detail, onClose, onSaved,
}: {
  detail: UnitDetailResponse | null
  onClose: () => void
  onSaved: () => void
}) {
  const [status, setStatus] = useState('ACTIVE')
  const [successorQuery, setSuccessorQuery] = useState('')
  const [results, setResults] = useState<UnitResponse[]>([])
  const [successor, setSuccessor] = useState<UnitResponse | null>(null)
  const [searching, setSearching] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setStatus(detail?.unit.status ?? 'ACTIVE')
    setSuccessor(null); setSuccessorQuery(''); setResults([]); setError(null)
  }, [detail])

  async function runSearch() {
    if (successorQuery.trim().length < 2) return
    setSearching(true)
    try {
      const found = await searchUnits(successorQuery.trim())
      setResults(found.filter((r) => r.id !== detail?.unit.id
        && r.unitType === detail?.unit.unitType))
    } catch (err) {
      setError(explain(err))
    } finally {
      setSearching(false)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const u = detail?.unit
    if (!u) return
    setBusy(true); setError(null)
    try {
      await changeUnitStatus(u.id, {
        status,
        mergedIntoId: status === 'MERGED' ? successor?.id ?? null : null,
      })
      onSaved()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const needsSuccessor = status === 'MERGED'

  return (
    <Modal
      open={Boolean(detail)}
      onClose={onClose}
      title={`Change status — ${detail?.unit.name ?? ''}`}
      description="Units are never deleted. Merging or dissolving keeps the record and its history."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button
            type="submit" form="status-form"
            className="erp-btn erp-btn-primary"
            disabled={busy || (needsSuccessor && !successor)}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shuffle className="h-3.5 w-3.5" />}
            Apply
          </button>
        </>
      }
    >
      <form id="status-form" onSubmit={submit} className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}

        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="erp-input w-full">
            <option value="ACTIVE">Active</option>
            <option value="PROPOSED">Proposed — not yet in force</option>
            <option value="MERGED">Merged into another unit</option>
            <option value="DISSOLVED">Dissolved</option>
          </select>
        </Field>

        {needsSuccessor && (
          <Field
            label="Merged into"
            required
            hint="Where this unit's records now point. Same unit type only."
          >
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  value={successorQuery}
                  onChange={(e) => setSuccessorQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void runSearch() } }}
                  placeholder="Search by name or code…"
                  className="erp-input flex-1"
                />
                <button type="button" className="erp-btn" onClick={runSearch} disabled={searching}>
                  {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Search'}
                </button>
              </div>

              {successor ? (
                <div className="flex items-center gap-2 px-3 py-2 border border-chocolate/40 bg-chocolate-soft/30 rounded-md">
                  <MapPin className="h-3.5 w-3.5 text-chocolate shrink-0" />
                  <span className="text-erp-sm flex-1 truncate">
                    {successor.name} <span className="font-mono text-erp-xs">{successor.code}</span>
                  </span>
                  <button type="button" className="erp-link text-erp-xs"
                          onClick={() => setSuccessor(null)}>
                    Change
                  </button>
                </div>
              ) : results.length > 0 ? (
                <ul className="border border-border rounded-md max-h-40 overflow-y-auto divide-y divide-border/60">
                  {results.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => setSuccessor(r)}
                        className="w-full px-3 py-2 text-left hover:bg-muted/50 flex items-center gap-2"
                      >
                        <span className="text-erp-sm truncate flex-1">{r.name}</span>
                        <span className="font-mono text-erp-xs text-muted-foreground">{r.code}</span>
                        <span className="text-erp-xs text-muted-foreground erp-metric">
                          {fmtNumber(r.registeredVoters)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </Field>
        )}
      </form>
    </Modal>
  )
}
